import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import HiringStats from './components/HiringStats';
import CandidatePipeline from './components/CandidatePipeline';
import VeteranVerification from './components/VeteranVerification';
import OnboardingWorkflow from './components/OnboardingWorkflow';

const VAHiringManagement = () => {
  const [selectedView, setSelectedView] = useState('pipeline');
  const [refreshing, setRefreshing] = useState(false);

  // Mock hiring statistics
  const hiringStats = {
    totalApplications: 156,
    activeInterviews: 23,
    pendingVerification: 12,
    onboardingInProgress: 8,
    placedVeterans: 89,
    clearanceVerified: 145
  };

  // Mock candidate data
  const mockCandidates = [
    {
      id: 1,
      name: 'John Martinez',
      email: 'john.martinez@email.com',
      militaryBranch: 'US Army',
      yearsOfService: 8,
      rank: 'Sergeant',
      clearanceLevel: 'Secret',
      skills: ['Project Management', 'Logistics', 'Team Leadership'],
      applicationStage: 'interview_scheduled',
      veteranStatus: 'verified',
      applicationDate: '2026-01-05',
      interviewDate: '2026-01-20'
    },
    {
      id: 2,
      name: 'Sarah Williams',
      email: 'sarah.williams@email.com',
      militaryBranch: 'US Navy',
      yearsOfService: 6,
      rank: 'Petty Officer',
      clearanceLevel: 'Top Secret',
      skills: ['Cybersecurity', 'Network Administration', 'Risk Assessment'],
      applicationStage: 'pending_review',
      veteranStatus: 'pending_verification',
      applicationDate: '2026-01-10',
      interviewDate: null
    },
    {
      id: 3,
      name: 'Michael Johnson',
      email: 'michael.johnson@email.com',
      militaryBranch: 'US Air Force',
      yearsOfService: 10,
      rank: 'Technical Sergeant',
      clearanceLevel: 'Secret',
      skills: ['Aircraft Maintenance', 'Quality Control', 'Safety Management'],
      applicationStage: 'approved',
      veteranStatus: 'verified',
      applicationDate: '2025-12-28',
      interviewDate: '2026-01-08'
    },
    {
      id: 4,
      name: 'Emily Davis',
      email: 'emily.davis@email.com',
      militaryBranch: 'US Marine Corps',
      yearsOfService: 5,
      rank: 'Corporal',
      clearanceLevel: 'Confidential',
      skills: ['Communications', 'Emergency Response', 'Training'],
      applicationStage: 'onboarding',
      veteranStatus: 'verified',
      applicationDate: '2025-12-15',
      interviewDate: '2025-12-22'
    }
  ];

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const breadcrumbItems = [
    { label: 'Roamsure', path: '/va-hiring-management' },
    { label: 'VA Hiring Management', path: '/va-hiring-management' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 px-6 pb-8">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} />

        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              VA Hiring Management
            </h1>
            <p className="text-muted-foreground">
              Streamline veteran recruitment and onboarding with specialized workflows
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="UserPlus"
            >
              Add Candidate
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
            >
              Export Report
            </Button>
          </div>
        </div>

        {/* Hiring Statistics */}
        <HiringStats stats={hiringStats} />

        {/* View Selector */}
        <div className="glass-container rounded-xl border border-border/50 p-4 mb-6">
          <div className="flex items-center space-x-2">
            {[
              { id: 'pipeline', label: 'Candidate Pipeline', icon: 'Users' },
              { id: 'verification', label: 'Veteran Verification', icon: 'Shield' },
              { id: 'onboarding', label: 'Onboarding Workflow', icon: 'CheckCircle' }
            ]?.map((view) => (
              <button
                key={view?.id}
                onClick={() => setSelectedView(view?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedView === view?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon name={view?.icon} size={16} />
                <span>{view?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Main View */}
          <div className="lg:col-span-2">
            {selectedView === 'pipeline' && <CandidatePipeline candidates={mockCandidates} />}
            {selectedView === 'verification' && <VeteranVerification candidates={mockCandidates} />}
            {selectedView === 'onboarding' && <OnboardingWorkflow candidates={mockCandidates?.filter(c => c?.applicationStage === 'onboarding')} />}
          </div>

          {/* Right Panel - Quick Actions & Info */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="glass-container rounded-xl border border-border/50">
              <div className="p-6 border-b border-border/50">
                <h3 className="text-lg font-semibold text-foreground">Quick Actions</h3>
              </div>
              <div className="p-6 space-y-3">
                {[
                  { id: 'schedule-interview', title: 'Schedule Interview', icon: 'Calendar', color: 'primary' },
                  { id: 'verify-clearance', title: 'Verify Clearance', icon: 'Shield', color: 'secondary' },
                  { id: 'bulk-status', title: 'Bulk Status Update', icon: 'CheckCircle', color: 'accent' },
                  { id: 'generate-report', title: 'Generate Report', icon: 'FileText', color: 'warning' }
                ]?.map((action) => (
                  <button
                    key={action?.id}
                    className={`w-full p-3 rounded-lg border transition-all duration-200 text-left hover:shadow-md text-${action?.color} bg-${action?.color}/10 border-${action?.color}/20 hover:bg-${action?.color}/20`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name={action?.icon} size={20} />
                      <span className="font-medium text-foreground">{action?.title}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Integration Status */}
            <div className="glass-container rounded-xl border border-border/50">
              <div className="p-6 border-b border-border/50">
                <h3 className="text-lg font-semibold text-foreground">Integration Status</h3>
              </div>
              <div className="p-6 space-y-4">
                {[
                  { name: 'USAJobs', status: 'connected', icon: 'Link' },
                  { name: 'Military Personnel DB', status: 'connected', icon: 'Database' },
                  { name: 'Security Clearance System', status: 'connected', icon: 'Shield' }
                ]?.map((integration) => (
                  <div key={integration?.name} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Icon name={integration?.icon} size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{integration?.name}</span>
                    </div>
                    <span className="px-2 py-1 rounded text-xs font-medium text-success bg-success/10">
                      {integration?.status?.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VAHiringManagement;